import { useTheme } from 'styled-components';

import { WalletTypeEnum } from '@libs/backend-common';
import {
  BlueDollarIcon,
  BlueGelIcon,
  DefaultTabItem,
  TableCell,
  Tabs,
  Typography,
} from '@libs/components';
import {
  formatBalance,
  getEstimatedAmount,
  getAmountPrefixAndColor,
  getDetailedFormatBalance,
} from '@libs/helpers';
import {
  CurrencySymbol,
  CurrencyType,
  HistoryPeriodEnumKeys,
  RealFiatAssets,
} from '@libs/types';

import { historyPeriodLabels } from 'src/constants';
import { getAlternativeAssetLabel } from 'src/helpers';
import { useTranslation, useWalletAssetsDetailed } from 'src/hooks';

import {
  BalanceWrapper,
  ContentBalancesWrapper,
  ContentContainer,
  ContentWrapper,
  CustomOperationsNavigation,
  IconWrapper,
  OperationsWrapper,
  Separator,
  TabsWrapper,
  TitleWrapper,
} from './styles';

type CoinBalanceProps = {
  assetCode: CurrencyType;
  activeCurrency: CurrencyType;
  handleTabClick: (val: string | number) => void;
};

const CoinBalance: React.FC<CoinBalanceProps> = ({
  assetCode,
  activeCurrency,
  handleTabClick,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const activeDurationKey = HistoryPeriodEnumKeys.ONE_DAY;

  const getBackgroundColor = (currency: CurrencyType.GEL | CurrencyType.USD) =>
    activeCurrency === currency
      ? colors.interactive.brandColor
      : colors.borders.default;

  const CurrencyTabs: (DefaultTabItem & {
    value: CurrencyType.GEL | CurrencyType.USD;
  })[] = [
    {
      title: '',
      id: CurrencyType.USD,
      img: (
        <IconWrapper>
          <BlueDollarIcon
            backgroundColor={getBackgroundColor(CurrencyType.USD)}
          />
        </IconWrapper>
      ),
      value: CurrencyType.USD,
    },
    {
      title: '',
      id: CurrencyType.GEL,
      img: (
        <IconWrapper>
          <BlueGelIcon backgroundColor={getBackgroundColor(CurrencyType.GEL)} />
        </IconWrapper>
      ),
      value: CurrencyType.GEL,
    },
  ];

  const { assets } = useWalletAssetsDetailed(
    WalletTypeEnum.ALL_ASSETS,
    activeCurrency as RealFiatAssets,
    assetCode,
    activeDurationKey
  );

  const assetData = assets.allAssets[0];
  const assetCodeLabel = getAlternativeAssetLabel(assetCode);

  const currencySymbol = CurrencySymbol[activeCurrency as RealFiatAssets];

  const rate = getDetailedFormatBalance(assetData?.value);
  const { changeColor, prefix, absolutePercentage, absoluteAmount } =
    getAmountPrefixAndColor(
      colors,
      assetData?.changeDetails.valueChangeAmount,
      assetData?.changeDetails.valueChangePercentage
    );

  return (
    <ContentWrapper>
      <TitleWrapper>
        <Typography
          type="subHeadline"
          fontFamily="primaryBold"
          color={colors.surface.default}
        >
          {t('common_my_asset_balance', { asset: assetCodeLabel })}
        </Typography>
      </TitleWrapper>

      <ContentContainer>
        <ContentBalancesWrapper>
          <BalanceWrapper>
            <TableCell
              titleType="primaryMedium"
              titleSize="titleTwo"
              descFont="primaryMedium"
              descType="bodyButton"
              color={colors.text.secondary}
              title={`${rate || '0.00'}${currencySymbol}`}
              description={`${getEstimatedAmount(
                assetData?.amount || '0'
              )} ${assetCodeLabel}`}
            />
            <TabsWrapper>
              <Tabs
                tabs={CurrencyTabs}
                activeTab={activeCurrency}
                handleTabClick={handleTabClick}
              />
            </TabsWrapper>
          </BalanceWrapper>

          <Separator />

          <BalanceWrapper>
            <Typography
              type="bodyButton"
              fontFamily="primaryMedium"
              color={changeColor}
            >
              {prefix}
              {`${absolutePercentage || 0}% 
              ${formatBalance(absoluteAmount, undefined, 2)}${currencySymbol}`}
            </Typography>
            <Typography
              type="caption"
              fontFamily="primaryMedium"
              color={colors.text.secondary}
            >
              {t('coin_details_change')} (
              {t(historyPeriodLabels[activeDurationKey])})
            </Typography>
          </BalanceWrapper>

          <Separator />

          <BalanceWrapper>
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {assetData?.percentage || '0'}%
            </Typography>
            <Typography
              type="caption"
              fontFamily="primaryMedium"
              color={colors.text.secondary}
            >
              {t('coin_details_share')}
            </Typography>
          </BalanceWrapper>
        </ContentBalancesWrapper>

        <OperationsWrapper>
          <CustomOperationsNavigation assetCode={assetCode} />
        </OperationsWrapper>
      </ContentContainer>
    </ContentWrapper>
  );
};

export default CoinBalance;
